'use client';

/**
 * Pluralsight-style proof band — dark gradient section with big
 * gradient-text stat values, label headlines, and explainer bodies.
 */
import { useTranslation } from 'react-i18next';
import { tx, asList } from '../_shared/coerce';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

interface StatItem {
  value?: string;
  label?: Localized;
  body?: Localized;
}

export interface PluralsightProofBandProps {
  title?: Localized;
  subtitle?: Localized;
  stats?: StatItem[];
}

/** Stock proof stats, used until the author supplies their own. Built
 *  per render so a language switch re-reads the catalogue. */
const defaultStats = (t: (key: string) => string): StatItem[] => [
  {
    value: '48%',
    label: t('proofBand.demo.blocked.label'),
    body: t('proofBand.demo.blocked.body'),
  },
  {
    value: '70%',
    label: t('proofBand.demo.faster.label'),
    body: t('proofBand.demo.faster.body'),
  },
  {
    value: '3,500+',
    label: t('proofBand.demo.handsOn.label'),
    body: t('proofBand.demo.handsOn.body'),
  },
];

export function PluralsightProofBand({ title, subtitle, stats }: PluralsightProofBandProps) {
  const { t } = useTranslation('site-builder-blocks');
  const list = asList<StatItem>(stats);
  const items = (list.length ? list : defaultStats(t)).slice(0, 4);

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'clamp(56px, 7vw, 96px) 24px',
        background: 'linear-gradient(135deg, #0b0b10, #18111f)',
        color: '#fff',
      }}
    >
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div
          className="ps-proof-head"
          style={{
            display: 'grid',
            gridTemplateColumns: '0.9fr 1.1fr',
            gap: 48,
            alignItems: 'end',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 'clamp(34px, 5vw, 64px)',
              lineHeight: 0.98,
              letterSpacing: 'var(--font-tracking-tight, -0.06em)',
              fontFamily: 'var(--font-family-display, Inter Tight, system-ui, sans-serif)',
              fontWeight: 900,
            }}
          >
            {tx(title) || t('proofBand.title')}
          </h2>
          {subtitle && (
            <p
              style={{
                margin: 0,
                color: 'rgba(255,255,255,0.7)',
                fontSize: 18,
                lineHeight: 1.55,
              }}
            >
              {tx(subtitle)}
            </p>
          )}
        </div>

        <div
          className="ps-proof-grid"
          style={{
            marginTop: 36,
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, minmax(0, 1fr))`,
            gap: 16,
          }}
        >
          {items.map((stat: StatItem, i: number) => (
            <div
              key={i}
              style={{
                padding: 24,
                borderRadius: 18,
                background: 'rgba(255,255,255,0.075)',
                border: '1px solid rgba(255,255,255,0.13)',
                minHeight: 220,
              }}
            >
              <div
                style={{
                  fontSize: 'clamp(42px, 5vw, 68px)',
                  lineHeight: 0.92,
                  letterSpacing: 'var(--font-tracking-tight, -0.06em)',
                  fontWeight: 900,
                  background: 'linear-gradient(90deg, #ff6b00, #ec008c)',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {stat.value}
              </div>
              <h3 style={{ margin: '22px 0 0', fontSize: 18, lineHeight: 1.2 }}>
                {tx(stat.label)}
              </h3>
              {stat.body && (
                <p
                  style={{
                    margin: '10px 0 0',
                    color: 'rgba(255,255,255,0.66)',
                    fontSize: 14,
                    lineHeight: 1.55,
                  }}
                >
                  {tx(stat.body)}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 860px) {
          .ps-proof-head { grid-template-columns: 1fr !important; }
          .ps-proof-grid { grid-template-columns: 1fr !important; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
